import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import { ArrowUp16Regular, ArrowDown16Regular, Subtract16Regular } from '@fluentui/react-icons';
import type { PeriodMetrics } from '@store/models';
import { computeMetricDelta, type ComparableMetricKey } from '@/types/metrics.types';
import { usePeriodComparisonStyles } from './PeriodComparison.styles';

/** Props for {@link PeriodComparison}. */
export interface PeriodComparisonProps {
  /** Exactly two periods to compare side by side (primary, comparison). */
  periods: [PeriodMetrics, PeriodMetrics];
}

interface MetricRowConfig {
  key: ComparableMetricKey;
  labelKey: string;
  isPercentage?: boolean;
}

const METRIC_ROWS: MetricRowConfig[] = [
  { key: 'requestedMeetings', labelKey: 'metrics.meetings' },
  { key: 'completedMeetings', labelKey: 'tile.completedLabel' },
  { key: 'requestedAttendees', labelKey: 'metrics.attendees' },
  { key: 'completedAttendees', labelKey: 'tile.completedLabel' },
  { key: 'meetingUtilizationRate', labelKey: 'metrics.meetingUtilization', isPercentage: true },
  { key: 'utilizationHoursPercentage', labelKey: 'metrics.utilizationHours', isPercentage: true },
];

function formatValue(value: number, isPercentage?: boolean): string {
  return isPercentage ? `${value.toFixed(1)}%` : value.toLocaleString();
}

/**
 * Renders two periods side by side with a display-only per-metric delta and
 * direction indicator. Deltas are computed client-side purely for display; the
 * underlying period values are always server-computed.
 */
export const PeriodComparison = ({ periods }: PeriodComparisonProps) => {
  const styles = usePeriodComparisonStyles();
  const { t } = useTranslation('metricsReport');
  const [first, second] = periods;

  const firstHeader = first.label ?? t('comparison.periodColumn', { index: 1 });
  const secondHeader = second.label ?? t('comparison.periodColumn', { index: 2 });

  return (
    <section className={styles.root} aria-label={t('comparison.heading')}>
      <h3 className={styles.heading}>{t('comparison.heading')}</h3>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} scope="col">
                {t('comparison.metricColumn')}
              </th>
              <th className={mergeClasses(styles.th, styles.thNumeric)} scope="col">
                {firstHeader}
              </th>
              <th className={mergeClasses(styles.th, styles.thNumeric)} scope="col">
                {secondHeader}
              </th>
              <th className={mergeClasses(styles.th, styles.thNumeric)} scope="col">
                {t('comparison.changeColumn')}
              </th>
            </tr>
          </thead>
          <tbody>
            {METRIC_ROWS.map((row) => {
              const baseValue = first[row.key];
              const comparisonValue = second[row.key];
              const delta = computeMetricDelta(baseValue, comparisonValue);
              const directionClass =
                delta.direction === 'up'
                  ? styles.deltaUp
                  : delta.direction === 'down'
                    ? styles.deltaDown
                    : styles.deltaFlat;
              const directionLabel =
                delta.direction === 'up'
                  ? t('comparison.increased')
                  : delta.direction === 'down'
                    ? t('comparison.decreased')
                    : t('comparison.unchanged');
              const DirectionIcon =
                delta.direction === 'up'
                  ? ArrowUp16Regular
                  : delta.direction === 'down'
                    ? ArrowDown16Regular
                    : Subtract16Regular;
              return (
                <tr key={row.key}>
                  <th scope="row" className={mergeClasses(styles.td, styles.metricName)}>
                    {t(row.labelKey)}
                  </th>
                  <td className={mergeClasses(styles.td, styles.tdNumeric)}>
                    {formatValue(baseValue, row.isPercentage)}
                  </td>
                  <td className={mergeClasses(styles.td, styles.tdNumeric)}>
                    {formatValue(comparisonValue, row.isPercentage)}
                  </td>
                  <td className={mergeClasses(styles.td, styles.tdNumeric, directionClass)}>
                    <DirectionIcon aria-hidden="true" />
                    <span aria-label={directionLabel}>
                      {' '}
                      {formatValue(Math.abs(delta.value), row.isPercentage)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

PeriodComparison.displayName = 'PeriodComparison';
